/**
 * Client-side thumbnail generation utility for diary photos
 * Phase 1 of hybrid thumbnail approach
 */

export interface ThumbnailResult {
  thumbnail: Blob;
  width: number;
  height: number;
  sizeBytes: number;
}

const THUMBNAIL_MAX_WIDTH = 400;
const THUMBNAIL_MAX_HEIGHT = 300;
const THUMBNAIL_QUALITY = 0.8;

/**
 * Generate a thumbnail from an image file
 * @param file - The original image file
 * @returns Promise<ThumbnailResult> - The generated thumbnail blob and metadata
 */
export async function generateThumbnail(file: File): Promise<ThumbnailResult> {
  return new Promise((resolve, reject) => {
    // Create image element
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    img.onload = () => {
      // Calculate dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > THUMBNAIL_MAX_WIDTH || height > THUMBNAIL_MAX_HEIGHT) {
        const aspectRatio = width / height;
        
        if (width > height) {
          width = THUMBNAIL_MAX_WIDTH;
          height = Math.round(width / aspectRatio);
        } else {
          height = THUMBNAIL_MAX_HEIGHT;
          width = Math.round(height * aspectRatio);
        }
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve({
              thumbnail: blob,
              width,
              height,
              sizeBytes: blob.size
            });
          } else {
            reject(new Error('Failed to generate thumbnail blob'));
          }
        },
        'image/jpeg',
        THUMBNAIL_QUALITY
      );

      // Cleanup
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(img.src);
    };

    // Load image
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Upload both full-size image and thumbnail to storage
 * @param file - Original image file
 * @param bucket - Storage bucket name
 * @param basePath - Base storage path (e.g., 'pins/', 'project123/')
 * @param supabase - Supabase client
 * @returns Promise with URLs for both full and thumbnail images
 */
export async function uploadWithThumbnail(
  file: File,
  bucket: string,
  basePath: string,
  supabase: any
): Promise<{
  fullUrl: string;
  thumbnailUrl: string;
  fullPath: string;
  thumbnailPath: string;
  srcSizeBytes: number;
}> {
  // Generate thumbnail
  const thumbnailResult = await generateThumbnail(file);

  // Generate unique filename
  const fileExt = file.name.split('.').pop();
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2);
  const baseFilename = `${timestamp}-${random}`;
  
  const fullPath = `${basePath}${baseFilename}.${fileExt}`;
  const thumbnailPath = `${basePath}${baseFilename}-thumb.jpg`;

  // Upload full-size image
  const { error: fullError } = await supabase.storage
    .from(bucket)
    .upload(fullPath, file);

  if (fullError) throw fullError;

  // Upload thumbnail
  const { error: thumbError } = await supabase.storage
    .from(bucket)
    .upload(thumbnailPath, thumbnailResult.thumbnail);

  if (thumbError) {
    // Clean up full image if thumbnail fails
    await supabase.storage.from(bucket).remove([fullPath]);
    throw thumbError;
  }

  // Get public URLs
  const { data: { publicUrl: fullUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(fullPath);

  const { data: { publicUrl: thumbnailUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(thumbnailPath);

  return {
    fullUrl,
    thumbnailUrl,
    fullPath,
    thumbnailPath,
    srcSizeBytes: file.size
  };
}
